import { useEffect, useRef, useState } from "react";

import { TextInput } from "../../fields/text-input";

export function CellTextInput<T extends object>({
  model,
  attr,
  type,
  width,
  disabled,
  multiline,
  required,
  className,
  placeholder,
}: {
  model: T;
  attr: keyof T;
  type?: "text" | "number";
  width?: number;
  disabled?: boolean;
  multiline?: boolean | "autosize";
  required?: boolean;
  className?: string;
  placeholder?: string;
}) {
  const { value, onChange } = useDebouncedValue(
    model[attr] as string,
    (v) => {
      model[attr] = v as T[keyof T];
    },
    100,
  );

  return (
    <TextInput
      placeholder={placeholder}
      className={className}
      type={type}
      value={value}
      onChange={onChange}
      width={width}
      disabled={disabled}
      multiline={multiline}
      required={required}
    />
  );
}

// Debounces model writes so that typing in table cells doesn't trigger a re-render on every keystroke.
function useDebouncedValue(
  modelValue: string,
  syncToModel: (value: string) => void,
  delay: number,
) {
  const [localValue, setLocalValue] = useState(modelValue ?? "");
  const isLocalEdit = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (!isLocalEdit.current) {
      setLocalValue(modelValue ?? "");
    }
    isLocalEdit.current = false;
  }, [modelValue]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const onChange = (newValue: string) => {
    isLocalEdit.current = true;
    setLocalValue(newValue);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      syncToModel(newValue);
    }, delay);
  };

  return { value: localValue, onChange };
}
